/** Non-UI helpers for the pull-plan grid: layout constants, greedy lane
 *  assignment, zone-row bookkeeping, and the seeding pass that turns a section
 *  context into the grid's initial rows / palette / instances / envelopes. */
import type { PullPlanSectionContext } from "../../../models";
import { hslToHex } from "../../../utils/color";
import { makeId } from "../../../utils/id";
import type { ActivityInstance, ZoneRow } from "../draft/types";
import { zoneKey } from "../draft/zones";
import type { ProtoNewAct } from "./PullPlanGrid";

/** A board bar's dep-graph id: a committed activity is its numeric id as a
 *  string; a draft-added bar (negative activity id) resolves to its draft id. */
export function barGraphId(protoNewActs: ProtoNewAct[], activityId: number): string {
  return activityId < 0
    ? protoNewActs[-activityId - 1]?.draftId ?? String(activityId)
    : String(activityId);
}

export interface PaletteActivity {
  activityKey: string;     // stable identity (committed Takt_Work value)
  name: string;            // displayed name
  color: string;           // hash-derived HSL
  default_duration: number;
  /** Displayed code value — diverges from activityKey only while a section-local
   *  re-code (pending_work_code_edits.new_code) is staged. Defaults to activityKey. */
  code?: string;
}

export const DAY_PX = 24;             // zoomed-out board — the whole span reads at once
export const LANE_HEIGHT = 20;        // height per stacked lane within a row
export const ZONE_LABEL_WIDTH = 264;
export const LANE_RAIL_WIDTH = 248;   // the left name/lane column; the day axis starts after it

/** Calendar column for an ISO date on an ascending workday axis (clamped). Exact
 *  match → its index; a non-workday date snaps to the PRIOR workday column;
 *  before the axis → 0, after → last. The single source of truth for board bar
 *  positioning — used by both the live board and the read-only completed view. */
export function indexOnAxis(calendar: string[], iso: string | null): number {
  if (!iso || calendar.length === 0) return 0;
  const d = iso.slice(0, 10);
  const exact = calendar.indexOf(d);
  if (exact >= 0) return exact;
  const next = calendar.findIndex((c) => c >= d);
  if (next === -1) return calendar.length - 1;
  return next === 0 ? 0 : next - 1;
}

/** Deterministic hex color from a string so the same Takt_Work always gets
 * the same color across page loads. Returns hex (not `hsl(...)`) so the
 * result is compatible with `autoFontColor`'s contrast check. */
export function hashColor(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  const hue = Math.abs(h) % 360;
  return hslToHex(hue, 65, 45);
}

export interface ScopeEnvelope {
  source_activity_id: number;
  name: string;
  start_index: number;
  duration_days: number;
  start_iso: string | null;
  end_iso: string | null;
}

export interface InitialState {
  rows: ZoneRow[];
  palette: PaletteActivity[];
  instances: ActivityInstance[];
  envelopes: ScopeEnvelope[];
}

/** Collapse rows that share a Takt zone (A/B/C) into a single row, remapping
 *  their instances onto the first occurrence. The grid shows ONE labeled block
 *  per zone — same-zone bars stack as lanes under it — instead of spreading the
 *  same zone across multiple duplicate-labeled rows. */
export function consolidateZones(
  rows: ZoneRow[],
  instances: ActivityInstance[],
): { rows: ZoneRow[]; instances: ActivityInstance[] } {
  const canonByZone = new Map<string, string>(); // zoneKey → kept rowId
  const remap = new Map<string, string>(); // dropped rowId → kept rowId
  const keptRows: ZoneRow[] = [];
  for (const r of rows) {
    const key = zoneKey(r.takt_a, r.takt_b, r.takt_c);
    const canon = canonByZone.get(key);
    if (canon == null) {
      canonByZone.set(key, r.rowId);
      keptRows.push(r);
    } else {
      remap.set(r.rowId, canon);
    }
  }
  if (remap.size === 0) return { rows, instances };
  const nextInstances = instances.map((i) =>
    remap.has(i.rowId) ? { ...i, rowId: remap.get(i.rowId) as string } : i,
  );
  return { rows: keptRows, instances: nextInstances };
}

/** Append a row for any target zone that doesn't already have one. Returns the
 *  (possibly extended) row list plus a zone-key → rowId map covering every
 *  target zone, so callers can place/move bars into the right rows. */
/** Case-folded zone identity for row matching — two spellings of the same
 *  typed level ("blka · lvl9" vs "BLKA · LVL9") must resolve to ONE row. */
export function foldedZoneKey(a: string, b: string, c: string): string {
  return zoneKey(a, b, c).toUpperCase();
}

export function ensureZoneRows(
  rows: ZoneRow[],
  zones: Array<{ takt_a: string; takt_b: string; takt_c: string }>,
): { rows: ZoneRow[]; rowIdByZone: Map<string, string> } {
  const nextRows = [...rows];
  const haveZone = new Set(rows.map((r) => foldedZoneKey(r.takt_a, r.takt_b, r.takt_c)));
  for (const z of zones) {
    const key = foldedZoneKey(z.takt_a, z.takt_b, z.takt_c);
    if (!haveZone.has(key)) {
      haveZone.add(key);
      nextRows.push({
        rowId: makeId("row"),
        takt_a: z.takt_a,
        takt_b: z.takt_b,
        takt_c: z.takt_c,
      });
    }
  }
  const rowIdByZone = new Map(
    nextRows.map((r) => [foldedZoneKey(r.takt_a, r.takt_b, r.takt_c), r.rowId]),
  );
  return { rows: nextRows, rowIdByZone };
}

export function isPlaceholder(a: PullPlanSectionContext["activities"][number]): boolean {
  return a.task_type === "TT_WBS" && /^\+\+\+/.test(a.task_name.trim());
}

/** True if the activity is a regular work task — the only type that's grid-
 *  eligible. Non-task activities (milestones, LOE, future P6 types) are not
 *  placed as bars; they still participate in the dep editor's scheduled graph.
 *  Case-normalised because XER files have both `TT_Task` and `TT_TASK`. */
export function isTaskType(a: PullPlanSectionContext["activities"][number]): boolean {
  return (a.task_type || "").toUpperCase() === "TT_TASK";
}

function isFullyTagged(a: PullPlanSectionContext["activities"][number]): boolean {
  // Fully tagged = every Takt dimension is set (section/A/B/C + work), a code was
  // derived, and it carries a sort rank — i.e. it renders as a real board bar.
  return Boolean(
    a.section_name && a.takt_a && a.takt_b && a.takt_c && a.takt_work && a.takt_code,
  ) && a.takt_sort_a != null;
}

export function seedInitialState(context: PullPlanSectionContext): InitialState {
  // Buckets by task_type + tagging state:
  //   - TT_WBS placeholders ("+++…")  → envelopes (scope strip at top)
  //   - TT_Task + fully Takt-tagged  → planActs (grid instances)
  //   - anything else (untagged tasks, milestones, LOE, …) → skipped here; the
  //     dep editor's scheduled graph surfaces them on its own.
  const planActs: PullPlanSectionContext["activities"] = [];
  for (const a of context.activities) {
    if (isPlaceholder(a)) continue;
    if (!isTaskType(a)) continue;
    if (!isFullyTagged(a)) continue;
    planActs.push(a);
  }

  // Distinct (Takt_A, Takt_B, Takt_C) → rowId map. Zones appear in the order
  // their first activity arrives from the backend, which already orders by
  // Takt_SortA → Takt_SortB → dates to match the Takt board. We deliberately
  // do NOT re-sort here — re-sorting client-side is what drifted the pull grid
  // out of sync with the board (see pull_plan_reference.md "Zone ordering").
  const rowIdByZone = new Map<string, string>();
  const rows: ZoneRow[] = [];
  for (const a of planActs) {
    const key = zoneKey(a.takt_a, a.takt_b, a.takt_c);
    if (rowIdByZone.has(key)) continue;
    const rowId = makeId("row");
    rowIdByZone.set(key, rowId);
    rows.push({ rowId, takt_a: a.takt_a, takt_b: a.takt_b, takt_c: a.takt_c });
  }

  // Distinct Takt_Work → palette chip. Built from ANY task-type activity that
  // carries a work code — not just fully-tagged planActs — so a freshly
  // work-coded (but not-yet-fully-tagged) activity still shows its code in the
  // palette right after Add Work Code. Key on the work code (stable across
  // schedules); display the friendly Takt_Prefix when available.
  const seen = new Set<string>();
  const palette: PaletteActivity[] = [];
  const codeColors = context.takt_work_colors || {};
  for (const a of context.activities) {
    if (!isTaskType(a) || isPlaceholder(a)) continue;
    if (!a.takt_work || seen.has(a.takt_work)) continue;
    seen.add(a.takt_work);
    palette.push({
      activityKey: a.takt_work,
      name: a.takt_prefix || a.takt_work,
      color: codeColors[a.takt_work] ?? hashColor(a.takt_work),
      default_duration: a.original_duration
        ? Math.max(1, Math.round(a.original_duration))
        : 1,
    });
  }
  palette.sort((p1, p2) => p1.name.localeCompare(p2.name));

  // O(1) calendar lookup; replaces N×indexOf(O(C)) inside the activity + envelope loops.
  const dayIndexByIso = new Map<string, number>();
  for (let i = 0; i < context.calendar.length; i++) {
    dayIndexByIso.set(context.calendar[i], i);
  }

  // Place existing real activities as instances
  const instances: ActivityInstance[] = [];
  for (const a of planActs) {
    if (!a.restart_date) continue;
    const rowId = rowIdByZone.get(foldedZoneKey(a.takt_a, a.takt_b, a.takt_c));
    if (!rowId) continue;
    const startIdx = dayIndexByIso.get(a.restart_date) ?? -1;
    if (startIdx < 0) continue;
    let duration = 1;
    if (a.reend_date) {
      const endIdx = dayIndexByIso.get(a.reend_date) ?? -1;
      if (endIdx >= startIdx) duration = endIdx - startIdx + 1;
    } else if (a.original_duration != null) {
      duration = Math.max(1, Math.round(a.original_duration));
    }
    instances.push({
      instanceId: makeId("inst"),
      rowId,
      activityKey: a.takt_work || "",
      start_index: startIdx,
      duration_days: duration,
      source_activity_id: a.id,
    });
  }

  // Scope envelopes — the section's TT_WBS placeholder(s)
  const envelopes: ScopeEnvelope[] = [];
  for (const a of context.activities) {
    if (!isPlaceholder(a)) continue;
    if (!a.restart_date) continue;
    const startIdx = dayIndexByIso.get(a.restart_date) ?? -1;
    if (startIdx < 0) continue;
    let duration = 1;
    if (a.reend_date) {
      const endIdx = dayIndexByIso.get(a.reend_date) ?? -1;
      if (endIdx >= startIdx) duration = endIdx - startIdx + 1;
    } else if (a.original_duration != null) {
      duration = Math.max(1, Math.round(a.original_duration));
    }
    envelopes.push({
      source_activity_id: a.id,
      name: a.task_name.replace(/\+/g, "").trim() || a.task_code,
      start_index: startIdx,
      duration_days: duration,
      start_iso: a.restart_date,
      end_iso: a.reend_date,
    });
  }

  return { rows, palette, instances, envelopes };
}
